import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import PrintableStructureReport from '../printable/PrintableStructureReport'; // Correct path
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Define the expected structure of the fetched report data
// (Should match the structure returned by getStructureReportDataForPrint)
interface StructureReportData {
  summary: {
    totalRevenue: number;
    averageOccupancyRate: number;
    totalStructures: number;
    totalOrders: number;
  };
  mostPopularStructures: Array<any>; // Use specific type if needed
  mostProfitableStructures: Array<any>; // Use specific type if needed
  structures: Array<{
    id: number;
    code: string;
    type: string;
    location: string;
    isLED: boolean;
    dailyPrice: number;
    monthlyPrice: number;
    occupancyRate: number;
    occupiedDays: number;
    totalDays: number;
    revenue: number;
    orderCount: number;
    avgDuration: number;
    clientCount: number;
    popularityRank: number;
    clientDistribution: Array<{ id: number; name: string; count: number }>;
  }>;
  startDate: string;
  endDate: string;
  selectedType?: string;
}

const StructureReportPrintPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const structureTypes = searchParams.get('structureTypes'); // Get optional type filter

  const [reportData, setReportData] = useState<StructureReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!startDate || !endDate) {
        setError('Не указан период для отчета.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Use the new backend endpoint for structure report print data
        const response = await axios.get<StructureReportData>(`/reports/structures/print-data`, {
          params: { startDate, endDate, structureTypes } // Pass filters
        });
        // Add startDate and endDate to the fetched data for the printable component
        // selectedType should already be included from the backend if structureTypes was passed
        setReportData({ ...response.data, startDate, endDate });
        // Attempt to trigger print dialog after data is loaded
        setTimeout(() => {
          // window.print();
        }, 500); // Small delay
      } catch (err: any) {
        console.error('Error fetching structure report data for printing:', err);
        setError(err.response?.data?.message || 'Не удалось загрузить данные отчета для печати.');
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [startDate, endDate, structureTypes]); // Depend on query parameters

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="printable-container error-message">Ошибка: {error}</div>;
  }

  if (!reportData) {
    return <div className="printable-container">Данные для печати не найдены.</div>;
  }

  // Render the printable component with the fetched data
  return <PrintableStructureReport reportData={reportData} />;
};

export default StructureReportPrintPage;
